import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from '@/lib/slugUtils';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import PageSpecificFAQStructuredData from '@/components/PageSpecificFAQStructuredData';
import { 
  Calculator, 
  TrendingUp, 
  Users, 
  Database, 
  Target,
  ArrowRight,
  Check,
  ChevronRight,
  Zap,
  LineChart,
  Share2,
  Search,
  Award,
  Trophy
} from 'lucide-react';

const whyIcons = [Target, Users, Database, TrendingUp, Trophy];
const seoIcons = [TrendingUp, Share2, Search, Award];

export default function InteractiveLeadGen() {
  const { t, i18n } = useTranslation('interactive-lead-gen');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleCTAClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        lang={currentLang}
        path={generateLocalizedUrl('interactive-lead-gen', currentLang)}
      />
      
      <Header />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <Breadcrumb />

          {/* Hero Section */}
          <section className="text-center mb-16 mt-12">
            <Badge variant="secondary" className="mb-6 text-base px-6 py-2" data-testid="badge-hero">
              <Calculator className="w-4 h-4 mr-2 inline" />
              {t('hero.badge')}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent" data-testid="title-hero">
              {t('hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-4 font-medium" data-testid="text-subtitle">
              {t('hero.subtitle')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto" data-testid="text-description">
              {t('hero.description')}
            </p>
            
            <Button size="lg" onClick={handleCTAClick} className="text-lg px-8 py-6" data-testid="button-cta-hero">
              {t('hero.cta')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </section>

          {/* What Is Section */}
          <Card className="mb-16">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Calculator className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-4" data-testid="title-what">
                    {t('what.title')}
                  </h2>
                  <p className="text-muted-foreground mb-4" data-testid="text-what-description">
                    {t('what.description')}
                  </p>
                  <p className="text-lg font-semibold text-primary" data-testid="text-what-conclusion">
                    {t('what.conclusion')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Why Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center" data-testid="title-why">
              {t('why.title')}
            </h2>
            
            <div className="space-y-6">
              {(t('why.items', { returnObjects: true }) as any[]).map((item, index) => {
                const Icon = whyIcons[index] || Target;
                return (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-4xl font-bold text-primary/20" data-testid={`number-why-${index}`}>
                              {item.number}
                            </span>
                            <h3 className="text-xl font-bold" data-testid={`title-why-${index}`}>
                              {item.title}
                            </h3>
                          </div>
                          <p className="text-muted-foreground mb-4" data-testid={`text-why-${index}`}>
                            {item.description}
                          </p>
                          {item.quote && (
                            <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                              <p>"{item.quote}"</p>
                              <footer className="text-sm mt-2">— {item.author}</footer>
                            </blockquote>
                          )}
                          {item.stats && (
                            <ul className="space-y-2 mt-4">
                              {item.stats.map((stat: string, i: number) => (
                                <li key={i} className="flex items-center gap-2">
                                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                                  <span>{stat}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Example Section */}
          <Card className="mb-16 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-4" data-testid="title-example">
                {t('example.title')}
              </h2>
              <p className="text-muted-foreground mb-4" data-testid="text-example-description">
                {t('example.description')}
              </p>
              <ul className="space-y-2 mb-6">
                {(t('example.actions', { returnObjects: true }) as string[]).map((action, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <ChevronRight className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-background/80 rounded-lg p-6">
                <h3 className="font-bold mb-3" data-testid="title-example-result">
                  {t('example.result.title')}
                </h3>
                <ul className="space-y-2 mb-4">
                  {(t('example.result.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground" data-testid="text-example-conclusion">
                  {t('example.result.conclusion')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Section */}
          <section className="mb-16">
            <Card>
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <LineChart className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold mb-2" data-testid="title-data">
                      {t('data.title')}
                    </h2>
                    <p className="text-muted-foreground" data-testid="text-data-intro">
                      {t('data.intro')}
                    </p>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  {(t('data.stats', { returnObjects: true }) as string[]).map((stat, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-lg">{stat}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg font-semibold text-primary" data-testid="text-data-conclusion">
                  {t('data.conclusion')}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* How It Works Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center" data-testid="title-how-it-works">
              {t('howItWorks.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {(t('howItWorks.steps', { returnObjects: true }) as any[]).map((step, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                        {step.number}
                      </div>
                      <h3 className="text-lg font-bold" data-testid={`title-step-${index}`}>
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-muted-foreground" data-testid={`text-step-${index}`}>
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Strategy Section */}
          <section className="mb-16">
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4" data-testid="title-strategy">
                  {t('strategy.title')}
                </h2>
                <p className="text-muted-foreground mb-6" data-testid="text-strategy-description">
                  {t('strategy.description')}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-bold">{t('strategy.table.headers.step')}</th>
                        <th className="text-left p-3 font-bold">{t('strategy.table.headers.action')}</th>
                        <th className="text-left p-3 font-bold">{t('strategy.table.headers.effect')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(t('strategy.table.rows', { returnObjects: true }) as any[]).map((row, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-3 font-bold text-primary">{row.step}</td>
                          <td className="p-3">{row.action}</td>
                          <td className="p-3 text-muted-foreground">{row.effect}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Types Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center" data-testid="title-types">
              {t('types.title')}
            </h2>
            <Card>
              <CardContent className="p-8">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-bold">{t('types.table.headers.type')}</th>
                        <th className="text-left p-3 font-bold">{t('types.table.headers.purpose')}</th>
                        <th className="text-left p-3 font-bold">{t('types.table.headers.example')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(t('types.table.rows', { returnObjects: true }) as any[]).map((row, index) => (
                        <tr key={index} className="border-b last:border-0">
                          <td className="p-3 font-bold text-primary">{row.type}</td>
                          <td className="p-3">{row.purpose}</td>
                          <td className="p-3 text-muted-foreground">{row.example}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* SEO Benefits Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-4 text-center" data-testid="title-seo">
              {t('seo.title')}
            </h2>
            <p className="text-center text-muted-foreground mb-8 max-w-3xl mx-auto" data-testid="text-seo-description">
              {t('seo.description')}
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {(t('seo.benefits', { returnObjects: true }) as any[]).map((benefit, index) => {
                const Icon = seoIcons[index] || Award;
                return (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-primary/10 rounded-lg">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold mb-2" data-testid={`title-seo-${index}`}>
                            {benefit.title}
                          </h3>
                          <p className="text-muted-foreground text-sm" data-testid={`text-seo-${index}`}>
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto" data-testid="text-seo-conclusion">
              {t('seo.conclusion')}
            </p>
          </section>

          {/* Why PriceForms Section */}
          <Card className="mb-16 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-4" data-testid="title-why-priceforms">
                    {t('whyPriceForms.title')}
                  </h2>
                  <p className="text-muted-foreground mb-6" data-testid="text-why-priceforms-description">
                    {t('whyPriceForms.description')}
                  </p>
                  <ul className="space-y-3">
                    {(t('whyPriceForms.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center" data-testid="title-faq">
              {t('faq.title')}
            </h2>
            <div className="space-y-4">
              {(t('faq.items', { returnObjects: true }) as any[]).map((item, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-bold mb-2 text-lg" data-testid={`question-${index}`}>
                      {item.question}
                    </h3>
                    <p className="text-muted-foreground" data-testid={`answer-${index}`}>
                      {item.answer}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Final CTA Section */}
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
            <CardContent className="p-8 text-center">
              <h2 className="text-3xl font-bold mb-4" data-testid="title-final-cta">
                {t('finalCta.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-6" data-testid="text-final-cta-description">
                {t('finalCta.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  size="lg" 
                  onClick={handleCTAClick}
                  className="text-lg px-8 py-6"
                  data-testid="button-cta-final"
                >
                  {t('finalCta.cta')} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <span className="text-muted-foreground">{t('finalCta.or')}</span>
                <Button 
                  size="lg" 
                  onClick={handleCTAClick}
                  variant="outline"
                  className="text-lg px-8 py-6"
                  data-testid="button-cta-secondary"
                >
                  {t('finalCta.secondaryCta')}
                </Button>
              </div>
              <p className="mt-6 text-sm text-muted-foreground" data-testid="text-guarantee">
                {t('finalCta.guarantee')}
              </p>
            </CardContent>
          </Card>

        </div>
      </div>
      
      <PageSpecificFAQStructuredData namespace="interactive-lead-gen" faqKey="faq.items" />
      <Footer />
    </>
  );
}
